import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`
            );
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

/* ===== Custom logic for Clip Case / Finish (Dwell) =====
   Requirements:
   - Option names must be exactly: "Clip Case" and "Finish"
   - Finish must contain value: "All Three"
   - Trio variant should be only: Trio + All Three
*/

(function () {
  function normalize(s) {
    return (s || "").trim().toLowerCase();
  }

  function getSelectedRadio(fieldset) {
    return fieldset?.querySelector('input[type="radio"]:checked') || null;
  }

  function setPickerMode(picker, isTrio) {
    if (!picker) return;
    picker.classList.toggle("is-trio", !!isTrio);
    picker.classList.toggle("is-not-trio", !isTrio);
  }

  function hideAllThreeWhenNotTrio(finishFieldset, isTrio) {
  const allThreeInput = finishFieldset?.querySelector('input[type="radio"][value="All Three"]');
  const allThreeLabel = allThreeInput?.closest("label");
  if (!allThreeLabel) return;

  // hide the whole tile, not just text
  allThreeLabel.hidden = !isTrio;
  allThreeLabel.style.display = isTrio ? "" : "none";
}

  function forceSelectAllThree(finishFieldset) {
    const allThree = finishFieldset?.querySelector('input[type="radio"][value="All Three"]');
    if (!allThree) return;

    if (!allThree.checked) {
      allThree.checked = true;
      // Trigger theme listeners
      allThree.dispatchEvent(new Event("input", { bubbles: true }));
      allThree.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  function applyClipFinishLogic(picker) {
    if (!picker) return;

    const clipFieldset = picker.querySelector('[data-option-name="Clip Case"]');
    const finishFieldset = picker.querySelector('[data-option-name="Finish"]');

    if (!clipFieldset || !finishFieldset) return;

    const selectedClip = getSelectedRadio(clipFieldset);
    if (!selectedClip) return;

    const isTrio = normalize(selectedClip.value) === "trio";

    // Set CSS mode first (prevents flicker)
    setPickerMode(picker, isTrio);

    // Show/hide "All Three" button itself depending on Trio
    hideAllThreeWhenNotTrio(finishFieldset, isTrio);

    // If Trio: hide Finish step and auto-select All Three
    if (isTrio) {
      forceSelectAllThree(finishFieldset);
    }
  }

  // Run on change inside any variant-picker
  document.addEventListener("change", (e) => {
    const picker = e.target.closest("variant-picker");
    if (!picker) return;
    applyClipFinishLogic(picker);
  });

  // Run once on load
  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("variant-picker").forEach((picker) => {
      applyClipFinishLogic(picker);
    });
  });

  // Optional: in case theme re-renders picker after variant updates
  // we re-apply logic shortly after load.
  window.setTimeout(() => {
    document.querySelectorAll("variant-picker").forEach((picker) => {
      applyClipFinishLogic(picker);
    });
  }, 200);
})();

(function () {
  const CLIP = "Clip Case";
  const FINISH = "Finish";
  const ALL_THREE = "All Three";
  const MAX_DUO = 2;

  function lower(s) { return (s || "").trim().toLowerCase(); }
  function trim(s) { return (s || "").trim(); }

  function getFieldset(picker, name) {
    return picker.querySelector(`[data-option-name="${name}"]`);
  }
  function getCheckedRadio(fieldset) {
    return fieldset?.querySelector('input[type="radio"]:checked') || null;
  }

  async function fetchProductJson(picker) {
    const url = picker.getAttribute("data-product-url");
    const res = await fetch(`${url}.js`);
    if (!res.ok) throw new Error("Failed to fetch product JSON");
    return await res.json();
  }

  function findVariantId(productJson, clipValue, finishValue) {
    const clip = trim(clipValue);
    const finish = trim(finishValue);
    const v = productJson.variants.find((variant) => trim(variant.option1) === clip && trim(variant.option2) === finish);
    return v?.id || null;
  }

  function setModeClasses(picker, mode) {
    picker.classList.toggle("is-trio", mode === "trio");
    picker.classList.toggle("is-not-trio", mode !== "trio");
    picker.classList.toggle("is-duo", mode === "duo");
    picker.classList.toggle("is-single", mode === "single");
  }

  function hideAllThreeTile(finishFieldset) {
    const input = finishFieldset?.querySelector(`input[value="${ALL_THREE}"]`);
    const label = input?.closest("label");
    if (label) label.style.display = "none";
  }

  function forceAllThreeWhenTrio(picker) {
    const finishFieldset = getFieldset(picker, FINISH);
    if (!finishFieldset) return;
    const allThree = finishFieldset.querySelector(`input[type="radio"][value="${ALL_THREE}"]`);
    if (allThree && !allThree.checked) {
      allThree.checked = true;
      allThree.dispatchEvent(new Event("input", { bubbles: true }));
      allThree.dispatchEvent(new Event("change", { bubbles: true }));
    }
  }

  function setFinishToRadioMode(finishFieldset) {
    // restore native radio behaviour (remove duo classes only)
    finishFieldset?.querySelectorAll("label.duo-selected").forEach(l => l.classList.remove("duo-selected"));
    finishFieldset?.querySelectorAll('input[type="radio"]').forEach((inp) => {
      inp.dataset.duoControlled = "false";
    });
  }

  function setFinishToDuoMultiMode(finishFieldset) {
    // mark inputs so we know we control them
    finishFieldset?.querySelectorAll('input[type="radio"]').forEach((inp) => {
      inp.dataset.duoControlled = "true";
    });

    // clear native checked state visuals, we will manage selected tiles
    finishFieldset?.querySelectorAll("label").forEach((l) => l.classList.remove("duo-selected"));
  }

  function getDuoSelectedFinishes(finishFieldset) {
    return Array.from(finishFieldset.querySelectorAll("label.duo-selected input[type='radio']"))
      .map(i => i.value)
      .filter(v => lower(v) !== lower(ALL_THREE));
  }

  function toggleDuoSelect(label, finishFieldset) {
    const input = label.querySelector("input[type='radio']");
    if (!input) return;
    if (lower(input.value) === lower(ALL_THREE)) return;

    const selected = getDuoSelectedFinishes(finishFieldset);

    const isAlready = label.classList.contains("duo-selected");
    if (isAlready) {
      label.classList.remove("duo-selected");
      return;
    }

    if (selected.length >= MAX_DUO) {
      // simple behaviour: remove the oldest selected
      const oldestLabel = finishFieldset.querySelector("label.duo-selected");
      if (oldestLabel) oldestLabel.classList.remove("duo-selected");
    }

    label.classList.add("duo-selected");
  }

  function getMode(picker) {
    const clipFieldset = getFieldset(picker, CLIP);
    const clipChecked = getCheckedRadio(clipFieldset);
    return lower(clipChecked?.value);
  }

  async function initPicker(picker) {
    const clipFieldset = getFieldset(picker, CLIP);
    const finishFieldset = getFieldset(picker, FINISH);
    if (!clipFieldset || !finishFieldset) return;

    // Hide All Three for non-trio always
    hideAllThreeTile(finishFieldset);

    let productJsonPromise = null;

    function applyMode() {
      const mode = getMode(picker); // single/duo/trio
      setModeClasses(picker, mode);

      if (mode === "trio") {
        // hide finish via your existing CSS (is-trio) and force all three
        forceAllThreeWhenTrio(picker);
        return;
      }

      if (mode === "duo") {
        setFinishToDuoMultiMode(finishFieldset);
      } else {
        setFinishToRadioMode(finishFieldset);
      }
    }

    // Intercept clicks on Finish when Duo: prevent radio switching, use multi select
    finishFieldset.addEventListener("click", (e) => {
      if (getMode(picker) !== "duo") return;

      const label = e.target.closest("label");
      if (!label || !finishFieldset.contains(label)) return;

      // stop native radio from changing variant
      e.preventDefault();
      e.stopPropagation();

      toggleDuoSelect(label, finishFieldset);
    });

    // Keep applying after clip changes
    picker.addEventListener("change", () => {
      applyMode();
      queueMicrotask(applyMode);
      setTimeout(applyMode, 0);
    });

    // Submit handling: Duo adds 2 items, Single uses native Shopify variant add
    document.addEventListener("submit", async (e) => {
      const form = e.target;
      if (!form.closest("variant-picker") || form.closest("variant-picker") !== picker) return;

      const mode = getMode(picker);
      if (mode !== "duo") return; // let normal submit happen

      e.preventDefault();

      const selectedFinishes = getDuoSelectedFinishes(finishFieldset);

      if (selectedFinishes.length !== 2) {
        alert("Please select two finishes for Duo.");
        return;
      }

      if (!productJsonPromise) productJsonPromise = fetchProductJson(picker);
      const productJson = await productJsonPromise;

      const id1 = findVariantId(productJson, "Duo", selectedFinishes[0]);
      const id2 = findVariantId(productJson, "Duo", selectedFinishes[1]);

      if (!id1 || !id2) {
        alert("Could not find Duo variants for the selected finishes. Check variant setup.");
        return;
      }

      const res = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            { id: id1, quantity: 1, properties: { "Duo set": "1/2", Finish: selectedFinishes[0] } },
            { id: id2, quantity: 1, properties: { "Duo set": "2/2", Finish: selectedFinishes[1] } }
          ]
        })
      });

      if (!res.ok) {
        alert("Failed to add Duo set to cart.");
        return;
      }

      // simplest: go to cart (later можем открыть drawer)
      window.location.href = "/cart";
    }, true);

    // initial
    applyMode();
  }

  document.addEventListener("DOMContentLoaded", () => {
    document.querySelectorAll("variant-picker").forEach(initPicker);
  });
})();

(() => {
  const keyFromOptionText = (txt) => {
    const v = String(txt || "").toLowerCase();

    // твои кнопки
    if (v.includes("patent black")) return "onyx";
    if (v.includes("gold shimmer")) return "golden";
    if (v.includes("vegan suede leopard")) return "leopard";

    // на всякий случай, если где то будет коротко
    if (v.includes("onyx")) return "onyx";
    if (v.includes("gold")) return "golden";
    if (v.includes("leopard")) return "leopard";

    return "";
  };

  const getOption1Value = (variantPickerEl) => {
    if (!variantPickerEl) return "";

    const checkedRadio = variantPickerEl.querySelector(
      'fieldset[data-fieldset-index="0"] input[type="radio"]:checked'
    );
    if (checkedRadio) return checkedRadio.value;

    const firstSelect = variantPickerEl.querySelector("select");
    if (firstSelect) return firstSelect.value;

    return "";
  };

  const applyFilter = (galleryEl, key) => {
    if (!galleryEl || !key) return;

    const nodes = galleryEl.querySelectorAll(
      ".slideshow__slide, slideshow-slide, li[ref='media[]'], .product-media-container"
    );

    let firstVisible = null;

    nodes.forEach((el) => {
      const elKey = (el.getAttribute("data-finish") || "").toLowerCase().trim();

      // если нет data-finish, не трогаем: видео, общие фото, и тд
      const show = !elKey || elKey === key;

      el.style.display = show ? "" : "none";
      el.setAttribute("aria-hidden", show ? "false" : "true");

      if (show && !firstVisible) firstVisible = el;
    });

    // попытка перескочить на первый подходящий слайд
    if (firstVisible) {
      const idx =
        firstVisible.getAttribute("data-index") ||
        firstVisible.dataset.index ||
        firstVisible.getAttribute("data-slide-index");

      if (idx != null) {
        const thumbBtn =
          galleryEl.querySelector(`button[on\\:click*="/handleThumbnailClick/${idx}"]`) ||
          galleryEl.querySelector(`button[data-slide-to="${idx}"]`);

        if (thumbBtn) thumbBtn.click();
      }
    }
  };

  const initGallery = (galleryEl) => {
    const variantPickerEl =
      galleryEl.closest(".shopify-section")?.querySelector("variant-picker") ||
      document.querySelector("variant-picker");

    if (!variantPickerEl) return;

    const run = () => {
      const optionText = getOption1Value(variantPickerEl);
      const key = keyFromOptionText(optionText);
      applyFilter(galleryEl, key);
    };

    run();

    variantPickerEl.addEventListener("change", () => setTimeout(run, 0), true);

    // если тема триггерит кастомные события
    document.addEventListener("variant:change", () => setTimeout(run, 0));
    document.addEventListener("variantChange", () => setTimeout(run, 0));
  };

  const boot = () => {
    document.querySelectorAll("media-gallery").forEach(initGallery);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();

(() => {
  const mapToKey = (txt) => {
    const v = String(txt || "").toLowerCase();

    if (v.includes("patent black")) return "onyx";
    if (v.includes("gold shimmer")) return "golden";
    if (v.includes("vegan suede leopard")) return "leopard";

    if (v.includes("onyx")) return "onyx";
    if (v.includes("gold")) return "golden";
    if (v.includes("leopard")) return "leopard";

    return "";
  };

  const labelFromKey = (key) => {
    if (key === "onyx") return "ONYX";
    if (key === "golden") return "GOLDEN";
    if (key === "leopard") return "LEOPARD";
    return "";
  };

  const imgFromKey = (key) => {
    // Эти файлы должны быть в Assets
    if (key === "onyx") return window.__finishTileOnyx || "";
    if (key === "golden") return window.__finishTileGolden || "";
    if (key === "leopard") return window.__finishTileLeopard || "";
    return "";
  };

  const setupGlobalsIfNeeded = () => {
    // Если не хочешь руками прописывать, оставь как есть и поставь реальные ссылки ниже в Liquid.
    // Лучше: вставить эти 3 строки в theme.liquid один раз (я напишу ниже).
    window.__finishTileOnyx = window.__finishTileOnyx || "";
    window.__finishTileGolden = window.__finishTileGolden || "";
    window.__finishTileLeopard = window.__finishTileLeopard || "";
  };

  const enhanceFinishFieldset = (variantPickerEl) => {
    // Finish у тебя второй опцией, значит fieldset-index="1"
    const fs = variantPickerEl.querySelector('fieldset[data-fieldset-index="1"]');
    if (!fs) return;

    fs.classList.add("finish-tiles");

    const radios = fs.querySelectorAll('input[type="radio"]');
    radios.forEach((input) => {
      const key = mapToKey(input.value);
      if (!key) return;

      const id = input.id;
      const label = fs.querySelector(`label[for="${CSS.escape(id)}"]`);
      if (!label) return;

      label.classList.add("finish-tile");
      label.setAttribute("data-finish", key);

      const imgUrl = imgFromKey(key);
      const niceLabel = labelFromKey(key);

      // Собираем разметку как в референсе
      label.innerHTML = `
        <div class="finish-tile__img" style="background-image: url('${imgUrl}')"></div>
        <div class="finish-tile__label">${niceLabel}</div>
      `;
    });
  };

  const boot = () => {
    setupGlobalsIfNeeded();

    document.querySelectorAll("variant-picker").forEach((vp) => {
      enhanceFinishFieldset(vp);

      // На случай динамических обновлений
      vp.addEventListener("change", () => {
        enhanceFinishFieldset(vp);
      }, true);
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})();